<script lang="ts">
	import { HighlightedText, Icon } from '@repo/commons/components'
	import { generateId } from '@repo/commons/utils'
	import type { NumberType } from './types.ts'
	import { clampNumber, sanitizeNumberDraft, resolveMask, type BuiltInMaskName } from './utils.ts'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	interface IconSnippet {
		component: any
		props?: Record<string, unknown>
		wrapperClass?: string
	}

	interface Props {
		label?: string
		isRequired?: boolean
		size?: 'xs' | 'sm' | 'md'
		inputElement?: HTMLInputElement | null
		element?: HTMLLabelElement | null
		highlightPattern?: string
		helperText?: string
		value?: string | number
		placeholder?: string
		type?: string
		readonly?: boolean
		disabled?: boolean
		clearable?: boolean
		prepend?: IconSnippet | null
		append?: IconSnippet | null
		extraAppend?: IconSnippet | null
		name?: string
		id?: string
		ariaActiveDescendant?: string
		showError?: boolean
		errorMessage?: string
		showWarning?: boolean
		warningMessage?: string
		autofocus?: boolean
		className?: string
		tooltip?: TooltipProps
		min?: number
		max?: number
		step?: number
		numberType?: NumberType
		mask?: BuiltInMaskName | ((value: string) => string)
		onInput?: (value: string) => void
		onClear?: () => void
		onFocus?: () => void
		onBlur?: () => void
		onClick?: (e: Event) => void
		onKeyDown?: (event: KeyboardEvent) => void
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void
	}

	let {
		label = '',
		size = 'sm',
		helperText = '',
		highlightPattern,
		isRequired = false,
		value = '',
		placeholder = '',
		type = 'text',
		errorMessage = '',
		showError = false,
		warningMessage = '',
		showWarning = false,
		readonly = false,
		disabled = false,
		clearable = false,
		autofocus = false,
		prepend = null,
		append = null,
		extraAppend = null,
		tooltip,
		min,
		max,
		step,
		name,
		id,
		ariaActiveDescendant,
		numberType,
		mask,
		className = '',
		element = $bindable(),
		inputElement = $bindable<HTMLInputElement | null>(null),
		onInput = () => {},
		onClear = () => {},
		onFocus = () => {},
		onBlur = () => {},
		onClick = () => {},
		onKeyDown = () => {},
		onHighlightMatch = () => {}
	}: Props = $props()

	const defaultId = id ?? generateId()
	const isNumberTypeActive = $derived(type === 'number' && !!numberType)

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })

	function handleInput(event: Event) {
		const inputElement = event.target as HTMLInputElement
		let next = inputElement.value

		if (isNumberTypeActive) {
			const allowNegative = min === undefined || min < 0
			next = sanitizeNumberDraft(next, numberType!, allowNegative)
		} else if (mask) {
			next = resolveMask(mask)(next)
		}

		value = next
		inputElement.value = next
		onInput(next)
	}

	function handleBlurInternal() {
		if (isNumberTypeActive) {
			const next = clampNumber(String(value ?? ''), min, max)

			if (next !== value) {
				value = next
				onInput(next)
			}
		}

		onBlur()
	}

	function clearInput(e: Event) {
		e.stopPropagation()
		e.preventDefault()
		value = ''
		onInput('')
		onClear()
		onBlur()
	}
</script>

<div class="w-full">
	<label bind:this={element} for={defaultId} class="label-wrapper">
		{#if label}
			<div class="label">
				{#if highlightPattern}
					<HighlightedText text={label} pattern={highlightPattern} {onHighlightMatch} />
				{:else}
					<span>{label}</span>
				{/if}
				{#if isRequired}
					<span class="label_required">*</span>
				{/if}

				{#if tooltip?.text || tooltip?.html}
					{#await import('@repo/commons/components') then { Tooltip }}
						<Tooltip {...tooltipProps}>
							<Icon name="info" size="sm" class="text-brand" />
						</Tooltip>
					{/await}
				{/if}
			</div>
		{/if}
		{#if helperText}
			<p class="helper-text">{helperText}</p>
		{/if}

		<div
			class={['input-wrapper', className]}
			class:hasLabel={label}
			class:invalid={showError}
			class:warning={!showError && showWarning}
			class:xs={size === 'xs'}
			class:sm={size === 'sm'}
			class:md={size === 'md'}
			class:disabled
		>
			{#if prepend}
				{@const Prepend = prepend.component}
				<div class="prepend-wrapper">
					<Prepend {...prepend.props ?? {}} />
				</div>
			{/if}

			<input
				bind:this={inputElement}
				id={defaultId}
				aria-activedescendant={ariaActiveDescendant}
				type={isNumberTypeActive ? 'text' : type}
				class="input-field flex-1 h-full px-4 py-2 outline-0 rounded-md"
				class:invalid={showError}
				class:has-prepended-element={prepend}
				class:is-clear-icon-shown={clearable && value && !disabled}
				class:time-input={type === 'time'}
				bind:value
				{placeholder}
				{autofocus}
				{readonly}
				{disabled}
				{name}
				{min}
				{max}
				{step}
				onclick={onClick}
				onkeydown={onKeyDown}
				oninput={handleInput}
				onfocus={onFocus}
				onblur={handleBlurInternal}
			/>

			{#if clearable && value && !disabled}
				<button
					type="button"
					class="clear-button"
					class:xs={size === 'xs'}
					class:has-appended-element={append}
					onclick={clearInput}
				>
					<Icon name="close" class="text-[#002C4D80]" size={size === 'md' ? 'md' : 'sm'} />
				</button>
			{/if}

			{#if extraAppend}
				{@const ExtraAppend = extraAppend.component}
				<div class={['append-wrapper', extraAppend.wrapperClass].filter(Boolean).join(' ')}>
					<ExtraAppend {...extraAppend.props ?? {}} />
				</div>
			{/if}
			{#if append}
				{@const Append = append.component}
				<div class="append-wrapper">
					<Append {...append.props ?? {}} />
				</div>
			{/if}
		</div>
	</label>

	{#if showError && errorMessage}
		<div class="error-message">
			<Icon name="fieldError" class="text-red-400" />
			<p class="error-text">{errorMessage}</p>
		</div>
	{:else if showWarning && warningMessage}
		<div class="warning-message">
			<Icon name="error" variant="warning" />
			<p class="warning-text">{warningMessage}</p>
		</div>
	{/if}
</div>

<style>
	.prepend-wrapper {
		@apply flex items-center ml-4;
	}
	.append-wrapper {
		@apply flex items-center mr-4;
	}
	.input-wrapper {
		@apply bg-white border-[1px] border-[#002C4D80] border-solid relative h-12 flex items-center w-full rounded-lg overflow-hidden;
		transition: outline-width 0.1s ease-in-out;
	}
	.input-wrapper.hasLabel {
		@apply mt-1;
	}
	.input-wrapper.disabled {
		@apply bg-gray-100;
	}
	.input-wrapper:has(:focus-visible):not(:has(:active)) {
		@apply outline outline-2 outline-brand outline-offset-2;
	}
	.input-wrapper.invalid:has(:focus-visible):not(:has(:active)) {
		@apply outline-[#E4485B]/30;
	}
	.input-wrapper.md {
		@apply h-12;
	}
	.input-wrapper.sm {
		@apply h-10;
	}
	.input-wrapper.xs {
		@apply h-8 text-sm;
	}
	.input-wrapper.xs .prepend-wrapper {
		@apply ml-3;
	}
	.input-wrapper.invalid {
		@apply border-[#E4485B] bg-[#E4485B]/5;
	}
	.input-wrapper.warning {
		@apply border-[#8F6C1A] bg-[#8F6C1A]/5;
	}
	.input-wrapper.warning:has(:focus-visible) {
		@apply outline-[#8F6C1A]/30;
	}
	.input-field {
		width: inherit;
		@apply pl-4 max-w-full bg-transparent outline-none;
	}
	.input-field.has-prepended-element {
		@apply pl-2;
	}
	.input-field.is-clear-icon-shown {
		@apply pr-2;
	}
	.input-field::placeholder {
		@apply text-[#001D33B2];
	}

	/* Hide the default clock icon for time inputs */
	.input-field.time-input::-webkit-calendar-picker-indicator {
		display: none;
		-webkit-appearance: none;
	}
	.input-field.time-input::-webkit-inner-spin-button,
	.input-field.time-input::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	.clear-button {
		@apply w-6 mr-4 h-6 text-lg;

		&.xs {
			@apply mr-2;
		}
	}
	.clear-button.has-appended-element {
		@apply mr-1;
	}
	.label-wrapper {
		@apply block;
	}
	.label {
		@apply flex items-center gap-1 text-base text-[#001626E5] leading-6;
	}

	.label_required {
		@apply text-[#001D33B2];
	}

	.helper-text {
		@apply text-sm text-[#001D33B2];
	}
	.error-message {
		@apply flex items-center min-h-8 my-1 text-red-400;
	}
	.error-text {
		@apply text-[#E4485B] text-sm font-semibold ml-2;
	}
	.warning-message {
		@apply flex items-center min-h-8 my-1;
	}
	.warning-text {
		@apply text-[#8F6C1A] text-sm font-semibold ml-2;
	}
</style>
